import { startTransition, useEffect, useEffectEvent, useState } from "react";
import { Nav, Tab } from "react-bootstrap";
import { PlusCircle } from "react-bootstrap-icons";
import { AsyncButton } from "../../controls";
import { getFromDataPath } from "../../utility";
import { Operation } from "../ars_ldm";
import { PATCH_ARS_STATE, useArsContext } from "../contexts";
import { isAnalysisMethod } from "../typeGuards";
import { OperationControl } from "./OperationControl";

export interface AnalysisMethodOperationsControlProps {
  /** RFC 6901 JSON Pointer path to the AnalysisMethod in ArsContext state. */
  dataPath: string;
  /** Disabled state. */
  disabled?: boolean;
}

/**
 * A component for managing the parameters of an AnalysisMethod.
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the AnalysisMethod in ArsContext state.
 * @param props.disabled - Optional flag to disable editing controls.
 * @returns A React functional component rendering the analysis method operations editor.
 */
export const AnalysisMethodOperationsControl = ({
  dataPath,
  disabled = false,
}: AnalysisMethodOperationsControlProps): React.ReactElement => {
  const { state, dispatch } = useArsContext();
  const analysisMethod = getFromDataPath(dataPath, state, isAnalysisMethod);
  const operations = analysisMethod?.operations ?? [];
  const [activeTab, setActiveTab] = useState<string>(operations.length > 0 ? "operation-0" : "");

  /**
   * Handles adding a new operation to the method.
   */
  const handleAddOperation = (): void => {
    /* istanbul ignore else -- Disabled buttons cannot invoke this handler from the UI */
    if (!disabled && analysisMethod) {
      const operationCount = operations.length;
      const newOperation: Operation = {
        id: crypto.randomUUID(),
        name: `Operation_${operationCount + 1}`,
        label: `Operation ${operationCount + 1}`,
        description: "",
        order: operationCount,
        referencedOperationRelationships: [],
        resultPattern: "",
      };

      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: `${dataPath}/operations/${operationCount}`,
        value: newOperation,
      });
    }
  };

  /**
   * Event handler to update the active tab.
   *
   * @param newTab - Tab key to activate.
   */
  const updateActiveTab = useEffectEvent((newTab: string) =>
    startTransition(async () => setActiveTab(newTab)),
  );

  /** Move tabs */
  useEffect(() => {
    if (parseInt(activeTab.split("-")[1], 10) >= operations.length) {
      updateActiveTab(operations.length > 0 ? "operation-0" : "");
    } else if (activeTab === "" && operations.length > 0) {
      updateActiveTab("operation-0");
    }
  }, [operations.length, activeTab]);

  const idRoute = dataPath.replace(/\//g, "-") + "-operations";

  return (
    <Tab.Container
      activeKey={activeTab}
      onSelect={(k) => {
        /* istanbul ignore else */
        if (k) {
          setActiveTab(k);
        }
      }}
    >
      <Nav
        variant="tabs"
        className="mb-3"
      >
        {operations.map((operation, index) => (
          <Nav.Item key={operation.id}>
            <Nav.Link eventKey={`operation-${index}`}>{operation.name}</Nav.Link>
          </Nav.Item>
        ))}
      </Nav>
      <Tab.Content>
        {operations.map((operation, index) => (
          <Tab.Pane
            key={operation.id}
            eventKey={`operation-${index}`}
          >
            <OperationControl
              dataPath={`${dataPath}/operations/${index}`}
              disabled={disabled}
              siblingCount={operations.length}
            />
          </Tab.Pane>
        ))}
      </Tab.Content>
      <AsyncButton
        id={`${idRoute}-add-operation`}
        onClick={handleAddOperation}
        className="d-flex align-items-center btn-sm btn-success m-2"
        disabled={disabled}
      >
        <span className="me-2">Add operation</span>
        <PlusCircle />
      </AsyncButton>
    </Tab.Container>
  );
};

AnalysisMethodOperationsControl.displayName = "AnalysisMethodOperationsControl";
